import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Check, ChevronLeft, ChevronRight, Loader2, Search, UserPlus } from 'lucide-react'
import { Modal } from '@/shared/ui/Modal'
import { Note } from '@/shared/ui/states'
import type { ContactChannelRequest, VoterLookupResponse } from '@/shared/api/types'
import { IdentityGate, useIdentityLookup } from './IdentityLookup'
import { explain } from '@/shared/api/problem'
import { LANGUAGE_LABEL, VOTER_LANGUAGES } from '@/shared/lib/language'
import { useDebounced } from '@/shared/hooks/useDebounced'
import { cn } from '@/lib/utils'
import { listUnits } from '@/features/geography/api'
import type { UnitResponse, VoterDetailResponse } from '@/shared/api/types'
import { createVoter, updateVoter } from './api'
import { DatePicker } from '@/components/erp/date-picker'

/**
 * Create a voter, or edit one.
 *
 * A wizard rather than one long form, for a reason that comes from the contract
 * rather than from taste: `CreateVoterRequest` requires `consentMethodCode`, so
 * **a voter cannot be created without recording how their consent to be held on
 * the register was captured.** That is a step in the process, not a checkbox at
 * the bottom of a form, and the shape of the dialog should say so.
 *
 * The four steps mirror the four things the backend needs to know:
 *   1. who they are        2. where they are registered
 *   3. where they live     4. how consent was captured
 *
 * Steps 3 is optional and can be skipped; 1, 2 and 4 cannot.
 *
 * **Editing is deliberately narrower than creating.** `PATCH /voters/{id}` refuses
 * a placement change with its own problem type — placement moves only through
 * `POST /voters/{id}/placement`, which writes history — so the edit form does not
 * offer station or ward at all. Rendering a field the API will reject is worse
 * than omitting it.
 */

const GENDERS = ['MALE', 'FEMALE', 'OTHER', 'UNDISCLOSED']
// Codes, in the backend enum's own order. The NAMES come from LANGUAGE_LABEL — sentence-
// casing a code gives "Kik" and "Luo", which is not what anybody calls those languages.
const LANGUAGES = [...VOTER_LANGUAGES]
const LITERACY = ['LITERATE', 'SEMI_LITERATE', 'ILLITERATE', 'UNRECORDED']
const SPECIAL_NEEDS = ['MOBILITY', 'VISUAL', 'HEARING', 'INTELLECTUAL', 'ELDERLY', 'PREGNANCY', 'OTHER']
const RESIDENCE_SOURCES = ['SELF_DECLARED', 'FIELD_CAPTURE', 'GPS_CAPTURE', 'IMPORT', 'INFERRED']

/** `ConsentMethod` from the shipped enum. `IMPORT_ATTESTED` is not offered here —
 *  it belongs to the importer, and a human at a keyboard is never "import attested". */
const CONSENT_METHODS: { code: string; label: string; hint: string }[] = [
  { code: 'SIGNATURE', label: 'Signature', hint: 'They signed a form, on paper or on a device' },
  { code: 'OTP', label: 'One-time code', hint: 'They confirmed with a code sent to their phone' },
  { code: 'VERBAL_WITNESSED', label: 'Verbal, witnessed', hint: 'They agreed aloud, with a named witness present' },
  { code: 'WEB_FORM', label: 'Web form', hint: 'They submitted the form themselves' },
  { code: 'USSD', label: 'USSD', hint: 'They confirmed through a USSD session' },
]

const STEPS = ['Identity', 'Registration', 'Residence', 'Consent'] as const

interface FormState {
  firstName: string; middleName: string; lastName: string
  nationalId: string; voterNumber: string
  genderCode: string; birthYear: string
  preferredLanguageCode: string; literacyCode: string; specialNeeds: string[]
  pollingStationId: string; pollingStationLabel: string; registeredAt: string
  residenceWardId: string; residenceDescription: string
  residenceSourceCode: string; residenceConfidence: string
  consentMethodCode: string; consentWitnessName: string
}

const EMPTY: FormState = {
  firstName: '', middleName: '', lastName: '', nationalId: '', voterNumber: '',
  genderCode: '', birthYear: '', preferredLanguageCode: '', literacyCode: '',
  specialNeeds: [], pollingStationId: '', pollingStationLabel: '', registeredAt: '',
  residenceWardId: '', residenceDescription: '', residenceSourceCode: '',
  residenceConfidence: '', consentMethodCode: '', consentWitnessName: '',
}

export function VoterFormDialog({
  open, onClose, onSaved, editing,
}: {
  open: boolean
  onClose: () => void
  onSaved: () => void
  /** When set the dialog edits; when null it creates. */
  editing?: VoterDetailResponse | null
}) {
  const isEdit = Boolean(editing)
  const navigate = useNavigate()
  const [step, setStep] = useState(0)
  // Only when creating: on an edit the national ID is already ours, and looking
  // it up again would be an external call that tells us nothing.
  // Keyed by the national ID it came from — not a bare boolean. See the
  // effect below: changing the ID drops it, so a prefill can never survive onto
  // a different person, and the next person's prefill is appliable again.
  const [appliedFor, setAppliedFor] = useState<string | null>(null)
  const [identitySource, setIdentitySource] = useState<
    {
      nationalId: string
      source: string
      ref?: string | null
      /** eCitizen's own `verified` flag. See the create payload for why it travels. */
      upstreamVerified: boolean
      channels: ContactChannelRequest[]
    } | null
  >(null)
  const [f, setF] = useState<FormState>(EMPTY)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  /**
   * Whether the rest of the form is shown.
   *
   * Creating starts as a single national-ID field: the register is asked about
   * the person before anyone is invited to describe them. Filling thirty fields
   * and only then learning the person is already registered is wasted work, and
   * it is how duplicates get made. Editing skips the gate entirely — the record
   * is already ours and there is nothing to look up.
   */
  const [revealed, setRevealed] = useState(false)
  /**
   * The national ID the fields below currently describe.
   *
   * A draft belongs to the person it was started for. Going back through the
   * gate and identifying somebody else must not leave the first person's name
   * sitting under the second person's ID — the provenance fields are already
   * keyed this way, and the visible fields have to be too, or the record is a
   * plausible-looking hybrid of two people. Re-searching the *same* ID keeps the
   * draft, because nothing about who it describes has changed.
   */
  const [draftFor, setDraftFor] = useState<string | null>(null)

  const lookup = useIdentityLookup()

  useEffect(() => {
    if (!open) return
    setError(null)
    setStep(0)
    setRevealed(Boolean(editing))
    setDraftFor(null)
    // Otherwise closing mid-search and reopening shows "Checking the register…"
    // over an empty gate, for a request nobody is waiting on any more.
    lookup.reset()
    setF(editing ? {
      ...EMPTY,
      firstName: editing.firstName ?? '',
      middleName: editing.middleName ?? '',
      lastName: editing.lastName ?? '',
      genderCode: editing.genderCode ?? '',
      birthYear: editing.birthYear ? String(editing.birthYear) : '',
      preferredLanguageCode: editing.preferredLanguageCode ?? '',
      literacyCode: editing.literacyCode ?? '',
      specialNeeds: editing.specialNeeds ?? [],
    } : EMPTY)
  }, [open, editing])

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) =>
    setF((prev) => ({ ...prev, [k]: v }))

  // Drop a previously applied prefill as soon as the ID stops matching it.
  // Without this the provenance and the contact channels of person A stay
  // attached while the operator types person B's ID.
  useEffect(() => {
    const id = f.nationalId.trim()
    if (appliedFor !== null && appliedFor !== id) setAppliedFor(null)
    if (identitySource && identitySource.nationalId !== id) setIdentitySource(null)
  }, [f.nationalId, appliedFor, identitySource])

  /**
   * Show the form.
   *
   *  - `'prefill'` — the prefill path has just written this person's details in;
   *    leave them alone.
   *  - `'blank'` — the operator asked for an empty form ("Start blank instead",
   *    "Continue and enter the details manually", "Register without a national
   *    ID"). It must be empty even if a previous prefill filled it, or the
   *    screen shows eCitizen's data while the payload says FIELD_CAPTURE — a
   *    record that looks government-sourced and is recorded as hand-typed.
   */
  const reveal = (id: string, mode: 'prefill' | 'blank') => {
    const next = id.trim()
    if (mode === 'blank') {
      setF({ ...EMPTY, nationalId: next })
      setAppliedFor(null)
      setIdentitySource(null)
    }
    setDraftFor(next)
    setRevealed(true)
    // The gate unmounts under the cursor, so focus would fall to <body> and a
    // keyboard user would have to tab in from the top of the dialog. Send it to
    // the first thing they now have to fill in.
    requestAnimationFrame(() => {
      document.getElementById('voter-first-name')?.focus()
    })
  }

  const applyPrefill = (r: VoterLookupResponse) => {
    const p = r.prefill
    if (!p) return
    // Only fill what is empty, and never clear something already typed: the
    // operator may have corrected a name the registry has wrong.
    //
    // Unless the draft describes somebody else. "Keep what is there" is the
    // right rule within one person and exactly the wrong one across two: after
    // Change → search a different ID → Use these details, honouring it would
    // hold on to the FIRST person's name and quietly refuse the second's.
    const forAnotherPerson = draftFor !== null && draftFor !== f.nationalId.trim()
    setF((prev) => {
      const base = forAnotherPerson ? { ...EMPTY, nationalId: f.nationalId } : prev
      return {
        ...base,
        firstName: base.firstName || (p.firstName ?? ''),
        middleName: base.middleName || (p.middleName ?? ''),
        lastName: base.lastName || (p.surname ?? ''),
        genderCode: base.genderCode || (p.genderCode ?? ''),
        birthYear: base.birthYear || (p.birthYear ? String(p.birthYear) : ''),
      }
    })
    setIdentitySource({
      nationalId: f.nationalId.trim(),
      source: 'ECITIZEN_IPRS',
      ref: r.upstreamRef,
      upstreamVerified: r.upstreamVerified,
      // A verified mobile number from a government source is the most
      // operationally useful field in the response — a campaign that cannot
      // phone a voter cannot mobilise one. It is sealed and blind-indexed
      // server-side exactly like the national ID.
      channels: ([
        p.mobileNumber
          ? { kindCode: 'PHONE', value: p.mobileNumber, primary: true, verified: p.mobileVerified }
          : null,
        p.email
          ? { kindCode: 'EMAIL', value: p.email, primary: false, verified: false }
          : null,
      ] as (ContactChannelRequest | null)[]).filter((c): c is ContactChannelRequest => c !== null),
    })
    setAppliedFor(f.nationalId.trim())
    reveal(f.nationalId, 'prefill')
  }

  const identityOk = f.firstName.trim() !== '' && f.lastName.trim() !== ''
  const registrationOk = isEdit || f.pollingStationId !== ''
  const consentOk = isEdit || f.consentMethodCode !== ''
  const canSave = identityOk && registrationOk && consentOk

  const save = async () => {
    setSaving(true)
    setError(null)
    // The eCitizen mobile number and email are only ours to store if the
    // operator accepted them for *this* person.
    const prefillForThisId =
      identitySource && identitySource.nationalId === f.nationalId.trim() ? identitySource : null
    try {
      if (editing) {
        // Placement fields are deliberately absent — PATCH refuses them.
        await updateVoter(editing.id, {
          firstName: f.firstName.trim(),
          middleName: f.middleName.trim() || null,
          lastName: f.lastName.trim(),
          genderCode: f.genderCode || null,
          birthYear: f.birthYear ? Number(f.birthYear) : null,
          preferredLanguageCode: f.preferredLanguageCode || null,
          literacyCode: f.literacyCode || null,
          specialNeeds: f.specialNeeds,
          // No provenance or channels here: UpdateVoterRequest declares neither,
          // so sending them is dropped today and would overwrite a record's real
          // provenance the day the backend accepts them.
        }, editing.version)
      } else {
        await createVoter({
          firstName: f.firstName.trim(),
          middleName: f.middleName.trim() || null,
          lastName: f.lastName.trim(),
          nationalId: f.nationalId.trim() || null,
          voterNumber: f.voterNumber.trim() || null,
          genderCode: f.genderCode || null,
          birthYear: f.birthYear ? Number(f.birthYear) : null,
          preferredLanguageCode: f.preferredLanguageCode || null,
          literacyCode: f.literacyCode || null,
          specialNeeds: f.specialNeeds,
          pollingStationId: f.pollingStationId,
          registeredAt: f.registeredAt || null,
          residenceWardId: f.residenceWardId || null,
          residenceDescription: f.residenceDescription.trim() || null,
          residenceSourceCode: f.residenceSourceCode || null,
          residenceConfidence: f.residenceConfidence ? Number(f.residenceConfidence) : null,
          // Provenance is modelled once, on the existing VoterSource enum plus
          // externalRef — not a second parallel set of identity fields. Both are
          // sent only when the prefill was accepted for the ID being submitted,
          // which is what stops one person's verified mobile number being
          // attached to another person's record.
          sourceCode: prefillForThisId ? prefillForThisId.source : 'FIELD_CAPTURE',
          externalRef: prefillForThisId?.ref ?? null,
          // This is the field that actually marks the voter verified. VoterService
          // records an IPRS_MATCH verification, and sets the status to
          // IPRS_MATCHED, only when sourceCode is ECITIZEN_IPRS *and* this is
          // true — so omitting it leaves every government-matched voter sitting
          // at UNVERIFIED however clearly the panel said "Government-verified".
          // It is eCitizen's assertion, not ours: false means the registry
          // returned the person without vouching for them, and the coordinator
          // still has to sight the ID.
          upstreamVerified: prefillForThisId?.upstreamVerified ?? false,
          contactChannels: prefillForThisId?.channels ?? [],
          consentMethodCode: f.consentMethodCode,
          consentWitnessName: f.consentWitnessName.trim() || null,
        })
      }
      onSaved()
      onClose()
    } catch (err) {
      setError(explain(err))
    } finally {
      setSaving(false)
    }
  }

  // Editing skips registration and consent — neither is changeable here.
  const steps = isEdit ? ['Identity'] : STEPS
  const last = step === steps.length - 1

  return (
    <Modal
      open={open}
      onClose={onClose}
      width="lg"
      title={isEdit ? `Edit ${editing?.displayName ?? 'voter'}` : 'Add a voter'}
      description={isEdit
        ? 'Name, demographics and accessibility. Placement changes go through a transfer, not an edit.'
        : !revealed
          ? 'Start with the national ID. We check the register — and eCitizen — before asking you to type anything else.'
          : 'Captured in the field. Consent to hold their record is required before the voter can be saved.'}
      footer={
        // Until the gate is passed the only actions belong to the gate itself —
        // a Next button beside a search box would imply the search is skippable
        // when it is the whole point of the screen.
        !revealed ? (
          <button type="button" className="erp-btn" onClick={onClose}>Cancel</button>
        ) : (
        <>
          {step > 0 && (
            <button type="button" className="erp-btn" onClick={() => setStep((n) => n - 1)} disabled={saving}>
              <ChevronLeft className="h-3.5 w-3.5" /> Back
            </button>
          )}
          <button type="button" className="erp-btn" onClick={onClose} disabled={saving}>Cancel</button>
          {!last ? (
            <button
              type="button"
              className="erp-btn erp-btn-primary"
              disabled={step === 0 && !identityOk}
              onClick={() => setStep((n) => n + 1)}
            >
              Next <ChevronRight className="h-3.5 w-3.5" />
            </button>
          ) : (
            <button
              type="button"
              className="erp-btn erp-btn-primary"
              disabled={!canSave || saving}
              onClick={() => void save()}
            >
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserPlus className="h-3.5 w-3.5" />}
              {isEdit ? 'Save' : 'Create voter'}
            </button>
          )}
        </>
        )
      }
    >
      {!isEdit && revealed && (
        <ol className="flex items-center gap-1 mb-4 text-erp-xs">
          {steps.map((label, i) => (
            <li key={label} className="flex items-center gap-1">
              <span className={cn(
                'inline-flex items-center gap-1.5 px-2 py-1 rounded-md',
                i === step ? 'bg-chocolate-soft/60 text-chocolate font-medium'
                  : i < step ? 'text-emerald-700 dark:text-emerald-400' : 'text-muted-foreground',
              )}>
                {i < step ? <Check className="h-3 w-3" /> : <span className="erp-metric">{i + 1}</span>}
                {label}
              </span>
              {i < steps.length - 1 && <ChevronRight className="h-3 w-3 text-muted-foreground" />}
            </li>
          ))}
        </ol>
      )}

      {error && <div className="mb-3"><Note tone="bad">{error}</Note></div>}

      {!isEdit && !revealed && (
        <IdentityGate
          value={f.nationalId}
          onChange={(v) => {
            set('nationalId', v)
            // The held answer is about a different number now.
            lookup.reset()
          }}
          state={lookup}
          onSearch={() => void lookup.search(f.nationalId)}
          onContinueBlank={() => reveal(f.nationalId, 'blank')}
          // A different act from "continue manually": the record is to have no
          // national ID at all, so the box is emptied rather than carried
          // forward. Sharing a handler once posted the ID of a person the gate
          // had just said was ALREADY REGISTERED — the exact duplicate it exists
          // to prevent.
          onRegisterWithoutId={() => reveal('', 'blank')}
          onUsePrefill={applyPrefill}
          onOpenExisting={(voterId: string) => {
            onClose()
            navigate(`/voters?open=${voterId}`)
          }}
        />
      )}

      {revealed && step === 0 && (
        <div className="space-y-3">
          <div className="grid grid-cols-3 gap-3">
            <Field
              id="voter-first-name"
              label="First name" required
              value={f.firstName} onChange={(v) => set('firstName', v)}
            />
            <Field label="Middle name" value={f.middleName} onChange={(v) => set('middleName', v)} />
            <Field label="Last name" required value={f.lastName} onChange={(v) => set('lastName', v)} />
          </div>
          {!isEdit && (
            <>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <span className="text-erp-xs text-muted-foreground">National ID</span>
                  {/* Settled at the gate, so it is shown rather than re-offered.
                      Editing it here would leave the form holding a prefill
                      fetched for a different number — the exact confusion the
                      gate exists to prevent — so changing it means going back
                      and searching again. */}
                  <div className="mt-1 flex items-center gap-2 h-9 px-3 rounded-md border border-input bg-muted/40">
                    <span className="font-mono text-erp-sm truncate flex-1">
                      {f.nationalId.trim() || <span className="text-muted-foreground font-sans">Not provided</span>}
                    </span>
                    <button
                      type="button"
                      className="text-erp-xs text-muted-foreground underline underline-offset-2 hover:text-foreground shrink-0"
                      onClick={() => {
                        // Only go back. Clearing provenance here would leave the
                        // fields visibly full of eCitizen's data while the
                        // payload claimed FIELD_CAPTURE; `reveal` decides what
                        // survives, once the operator has said what they meant.
                        setRevealed(false)
                        lookup.reset()
                      }}
                    >
                      Change
                    </button>
                  </div>
                </div>
                <Field label="Voter number" value={f.voterNumber} onChange={(v) => set('voterNumber', v)} mono />
              </div>
              <p className="text-erp-xs text-muted-foreground">
                {appliedFor && appliedFor === f.nationalId.trim()
                  ? 'Prefilled from eCitizen. Every field below is yours to correct — the registry is often out of date on names.'
                  : 'The national ID is encrypted on arrival and indexed blind: matched exactly, never searched partially.'}
              </p>
            </>
          )}
          <div className="grid grid-cols-3 gap-3">
            <Select label="Gender" value={f.genderCode} onChange={(v) => set('genderCode', v)} options={GENDERS} />
            <Field
              label="Birth year" value={f.birthYear} onChange={(v) => set('birthYear', v)}
              type="number" placeholder="1988"
            />
            <Select
              label="Language" value={f.preferredLanguageCode}
              onChange={(v) => set('preferredLanguageCode', v)} options={LANGUAGES}
              labels={LANGUAGE_LABEL}
            />
          </div>
          <p className="text-erp-xs text-muted-foreground">
            Year of birth only — enough to band an age, and a weaker identifier than a full
            date of birth would be.
          </p>
          <Select label="Literacy" value={f.literacyCode} onChange={(v) => set('literacyCode', v)} options={LITERACY} />
          <div>
            <span className="text-erp-xs text-muted-foreground">Accessibility needs</span>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {SPECIAL_NEEDS.map((n) => {
                const on = f.specialNeeds.includes(n)
                return (
                  <button
                    key={n}
                    type="button"
                    onClick={() => set('specialNeeds', on
                      ? f.specialNeeds.filter((x) => x !== n)
                      : [...f.specialNeeds, n])}
                    className={cn(
                      'px-2 py-1 text-erp-xs border rounded-md',
                      on ? 'border-chocolate bg-chocolate-soft/50 text-chocolate' : 'border-input text-muted-foreground',
                    )}
                  >
                    {n[0] + n.slice(1).toLowerCase().replace(/_/g, ' ')}
                  </button>
                )
              })}
            </div>
          </div>
        </div>
      )}

      {step === 1 && !isEdit && (
        <div className="space-y-3">
          <StationPicker
            value={f.pollingStationId}
            label={f.pollingStationLabel}
            onPick={(id, label) => { set('pollingStationId', id); set('pollingStationLabel', label) }}
          />
          <Field label="Registered on" type="date" value={f.registeredAt} onChange={(v) => set('registeredAt', v)} />
          <p className="text-erp-xs text-muted-foreground">
            The polling station is where they are <em>registered</em>. Where they live is the
            next step, and the two are allowed to differ — that gap is what a transfer
            request is built on.
          </p>
        </div>
      )}

      {step === 2 && !isEdit && (
        <div className="space-y-3">
          <Field
            label="Address or landmark" value={f.residenceDescription}
            onChange={(v) => set('residenceDescription', v)}
            placeholder="Off Ngong Road, near the shopping centre"
          />
          <div className="grid grid-cols-2 gap-3">
            <Select
              label="How was this captured?" value={f.residenceSourceCode}
              onChange={(v) => set('residenceSourceCode', v)} options={RESIDENCE_SOURCES}
            />
            <Field
              label="Confidence (0–100)" type="number" value={f.residenceConfidence}
              onChange={(v) => set('residenceConfidence', v)} placeholder="70"
            />
          </div>
          <p className="text-erp-xs text-muted-foreground">
            Optional. Skip it rather than guess — a low-confidence address recorded as fact
            is worse than no address.
          </p>
        </div>
      )}

      {step === 3 && !isEdit && (
        <div className="space-y-3">
          <p className="text-erp-sm">
            How did this person agree to being held on the register?
          </p>
          <div className="space-y-1.5">
            {CONSENT_METHODS.map((m) => (
              <button
                key={m.code}
                type="button"
                onClick={() => set('consentMethodCode', m.code)}
                className={cn(
                  'w-full text-left px-3 py-2 border rounded-md',
                  f.consentMethodCode === m.code
                    ? 'border-chocolate bg-chocolate-soft/40'
                    : 'border-input hover:bg-muted/40',
                )}
              >
                <div className="text-erp-sm font-medium">{m.label}</div>
                <div className="text-erp-xs text-muted-foreground">{m.hint}</div>
              </button>
            ))}
          </div>
          {f.consentMethodCode === 'VERBAL_WITNESSED' && (
            <Field
              label="Witness name" required value={f.consentWitnessName}
              onChange={(v) => set('consentWitnessName', v)}
            />
          )}
          <Note tone="info">
            This is recorded as an append-only consent row. A later withdrawal adds another
            row rather than deleting this one, so the record of the grant survives.
          </Note>
        </div>
      )}
    </Modal>
  )
}

/** Type-ahead over `GET /geography/units`, restricted to polling stations. */
function StationPicker({
  value, label, onPick,
}: { value: string; label: string; onPick: (id: string, label: string) => void }) {
  const [q, setQ] = useState('')
  const debounced = useDebounced(q, 300)
  const [results, setResults] = useState<UnitResponse[]>([])
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (debounced.trim().length < 2) { setResults([]); return }
    let cancelled = false
    setLoading(true)
    listUnits({ q: debounced, type: ['POLLING_STATION'] } as never, 0, 12)
      .then((page) => { if (!cancelled) setResults(page.content) })
      .catch(() => { if (!cancelled) setResults([]) })
      .finally(() => { if (!cancelled) setLoading(false) })
    return () => { cancelled = true }
  }, [debounced])

  return (
    <div>
      <span className="text-erp-xs text-muted-foreground">
        Polling station <span className="text-red-600">*</span>
      </span>
      {value ? (
        <div className="mt-1 flex items-center gap-2 px-3 h-9 border border-chocolate/50 rounded-md bg-chocolate-soft/30">
          <Check className="h-3.5 w-3.5 text-chocolate shrink-0" />
          <span className="text-erp-sm truncate flex-1">{label}</span>
          <button
            type="button"
            className="text-erp-xs text-muted-foreground hover:text-foreground shrink-0"
            onClick={() => onPick('', '')}
          >
            Change
          </button>
        </div>
      ) : (
        <>
          <div className="mt-1 flex items-center gap-2 px-3 h-9 border border-input rounded-md bg-background">
            <Search className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search stations by name or code"
              className="bg-transparent outline-none text-erp-sm w-full min-w-0"
            />
            {loading && <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground shrink-0" />}
          </div>
          {results.length > 0 && (
            <div className="mt-1 erp-card max-h-48 overflow-y-auto py-1">
              {results.map((u) => (
                <button
                  key={u.id}
                  type="button"
                  onClick={() => onPick(u.id, `${u.name} (${u.code})`)}
                  className="w-full px-3 py-1.5 text-left text-erp-sm hover:bg-muted"
                >
                  {u.name}
                  <span className="text-erp-xs text-muted-foreground font-mono ml-2">{u.code}</span>
                </button>
              ))}
            </div>
          )}
          {debounced.trim().length >= 2 && !loading && results.length === 0 && (
            <div className="mt-1 text-erp-xs text-muted-foreground">No stations match.</div>
          )}
        </>
      )}
    </div>
  )
}

function Field({
  label, value, onChange, required, type = 'text', placeholder, mono, id,
}: {
  label: string; value: string; onChange: (v: string) => void
  required?: boolean; type?: string; placeholder?: string; mono?: boolean
  /** Only set where something needs to move focus here. */
  id?: string
}) {
  return (
    <label className="block">
      <span className="text-erp-xs text-muted-foreground">
        {label}{required && <span className="text-red-600"> *</span>}
      </span>
      {type === 'date'
        ? <div className="mt-1">
            <DatePicker id={id} value={value} onChange={onChange} placeholder={placeholder} />
          </div>
        : <input
            id={id}
            type={type}
            value={value}
            placeholder={placeholder}
            onChange={(e) => onChange(e.target.value)}
            className={cn('erp-input w-full mt-1', mono && 'font-mono')}
          />}
    </label>
  )
}

/**
 * `labels` is for vocabularies whose codes are not words.
 *
 * Sentence-casing a code is right for GENDER and LITERACY — MALE reads "Male", NO_FORMAL
 * reads "No formal" — and wrong for anything abbreviated: it turns KIK into "Kik" and SW
 * into "Sw". Pass a code→name map and the option shows the name; the value posted is still
 * the code.
 */
function Select({
  label, value, onChange, options, labels,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  options: string[]
  labels?: Record<string, string>
}) {
  return (
    <label className="block">
      <span className="text-erp-xs text-muted-foreground">{label}</span>
      <select className="erp-input w-full mt-1" value={value} onChange={(e) => onChange(e.target.value)}>
        <option value="">—</option>
        {options.map((o) => (
          <option key={o} value={o}>
            {labels?.[o] ?? o[0] + o.slice(1).toLowerCase().replace(/_/g, ' ')}
          </option>
        ))}
      </select>
    </label>
  )
}
